/* ══ v11 (◆C): create a programme — it is a RECORD now, not a file ════════════════════
   v10.5 needed `showSaveFilePicker`, a visible folder warning and a slug naming rule
   because a new programme meant a new FILE, and the user could save it in the wrong
   place. Under one file it is an array push, so this reuses the `ovNewProject` gesture
   exactly: click, type, Enter, Escape cancels. Retired with it: `pfCreateProgramme`,
   `_progSlug`, `_progFileNameFor` and the folder hint they existed to justify. */
function pfNewProgramme(btn) {
  let done = false;
  const input = document.createElement('input');
  input.className = 'ov-new-input';
  input.type = 'text';
  input.placeholder = 'New programme name…';
  input.maxLength = 80;
  const commit = (cancel) => {
    if (done) return; done = true;               /* one-shot: Enter-then-blur must not double-create */
    const v = (input.value || '').trim();
    if (cancel || !v) { renderPortfolio(); return; }
    const id = createProgramme(v);
    if (!id) { renderPortfolio(); return; }
    markDirty();
    selectProgramme(id);                          /* drop straight into the new programme */
  };
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(false); }
    if (e.key === 'Escape') { e.preventDefault(); commit(true); }
  });
  _onBlurCommit(input, () => commit(false));
  btn.replaceWith(input);
  input.focus();
}

/* v10.3 (W5): _landingSwitch removed — the static .ls-seg bar in the markup
   replaced this generated one; nothing called it. */
/* effective action view for ANY project (edit overlay with base fallback) */
function _fxEff(a, ed) {
  const e = (ed && ed[a.id]) || {};
  const status = e.status || (typeof STATUS_DISPLAY === 'object' && STATUS_DISPLAY[a.status]) || a.status;
  const priority = e.priority || (typeof PRIORITY_DISPLAY === 'object' && PRIORITY_DISPLAY[a.priority]) || a.priority;
  const due = (e.due !== undefined && e.due !== null) ? e.due : (a.due || '');
  const flagged = (e.flagged !== undefined) ? (e.flagged === true) : (a.flagged === true);
  const dependsOn = Array.isArray(e.dependsOn) ? e.dependsOn : (Array.isArray(a.dependsOn) ? a.dependsOn : []);
  const desc = actField(e, a, 'desc') || '';
  return { status, priority, due, flagged, dependsOn, desc };
}
/* v13.1 (A52): the Focus view had its own third spelling of "closed", listing the two
   statuses by hand. CLOSED_STATUSES is the one place that knows them; a status added there
   would have silently missed this copy. */
function _fxOpen(st) { return !isClosedStatus(st); }
function _fxPriRank(p) { const m = { High:0, Medium:1, Low:2 }; return (m[p] != null) ? m[p] : 3; }
function _fxPriClass(p) { return p === 'High' ? 'h' : (p === 'Medium' ? 'm' : 'l'); }
function _fxToday() { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }
/* v10.3 (W4): format LOCALLY. This used to be `d.toISOString().slice(0,10)` applied to a
   LOCAL-midnight Date — and toISOString converts to UTC, so at any positive offset
   (CET/CEST, i.e. every day here) local midnight is 22:00/23:00 UTC on the PREVIOUS day.
   The Focus view's "today" was therefore yesterday, all day, every day: an action due
   today read as "1 day over", the Overdue bucket only caught items ≥2 days late, and the
   due-this-week window was [yesterday … +6 days]. Worse, the per-project views use a
   different helper, so the two disagreed about what was overdue. */
function _fxISO(d) { return _isoLocal(d); }
function _fxJsAttr(s) { return (typeof _escapeHtml === 'function') ? _escapeHtml(String(s)) : String(s); }

function _fxDueLabel(due, todayStr) {
  if (!due) return '';
  if (due < todayStr) {
    const days = Math.round((Date.parse(todayStr) - Date.parse(due)) / 86400000);
    return days <= 0 ? 'today' : (days === 1 ? '1 day over' : days + ' days over');
  }
  if (typeof _parseDueDate === 'function' && typeof _fmtCompactDate === 'function') {
    const d = _parseDueDate(due); if (d) return _fmtCompactDate(d);
  }
  return due;
}

/* Build the seven buckets across all non-archived projects. */
function _focusData() {
  const db = window.__DB__ || {};
  const custs = _progProjects();   /* v11: Focus stays scoped to the ACTIVE programme */
  const today = _fxToday(), todayStr = _fxISO(today);
  const plus7 = new Date(today); plus7.setDate(plus7.getDate() + 7); const plus7Str = _fxISO(plus7);
  const quietCut = new Date(today); quietCut.setDate(quietCut.getDate() - 42); const quietStr = _fxISO(quietCut);
  const B = { overdue: [], dueweek: [], highpri: [], blocked: [], agenda: [], risks: [], quiet: [] };

  custs.forEach(c => {
    const ed = (c.edits && typeof c.edits === 'object') ? c.edits : {};
    /* ⚠ v15.2: the SESSION'S OWN pending state was invisible here — this read raw c.actions,
       so an action deleted this session (still in the array until Save bakes it; only
       `_deletedIds` knows) kept showing in Overdue and offered a dead-end jump, a deleted
       blocker went on "blocking", the quiet-bucket open count was inflated — and an action
       ADDED this session (living only in newActions) never appeared even when due today.
       Exactly the class A19 fixed for RAID Dependencies: read through the same lens the
       Actions tab reads through. */
    const dead = (c._deletedIds instanceof Set) ? c._deletedIds : null;
    const actions = (Array.isArray(c.actions) ? c.actions : [])
      .concat(Array.isArray(c.newActions) ? c.newActions : [])
      .filter(a => a && !(dead && dead.has(a.id)));
    const effById = new Map();
    actions.forEach(a => effById.set(a.id, _fxEff(a, ed)));
    const blocked = (f) => {
      if (!f.dependsOn.length) return false;
      for (const bid of f.dependsOn) { const be = effById.get(bid); if (be && _fxOpen(be.status)) return true; }
      return false;
    };
    actions.forEach(a => {
      const f = effById.get(a.id);
      if (!_fxOpen(f.status)) return;  // action buckets are open-only
      const it = { custId: c.id, custName: c.name || 'Untitled project', actionId: a.id,
                   title: f.desc || '(untitled action)', priority: f.priority, status: f.status, due: f.due };
      if (f.due && f.due < todayStr) B.overdue.push(it);
      if (f.due && f.due >= todayStr && f.due <= plus7Str) B.dueweek.push(it);
      if (f.priority === 'High') B.highpri.push(it);
      if (blocked(f)) B.blocked.push(it);
      if (f.flagged) B.agenda.push(it);
    });
    (Array.isArray(c.risks) ? c.risks : []).forEach(r => {
      if (!r || (r.status || 'Open') === 'Closed' || r.type === 'opportunity') return;
      /* v13.1 (A37): was a third copy of p×i behind `typeof _riskScoreInfo === 'function'`
         — a guard on our own hoisted function, which can only hide its absence. */
      const score = _riskScore(r);
      if (score < 6) return;
      B.risks.push({ custId: c.id, custName: c.name || 'Untitled project',
                     title: r.statement || r.title || '(risk)', score,
                     imp: _RISK_RANK[r.impact] || 0, id: (typeof r.id === 'number') ? r.id : Number.MAX_SAFE_INTEGER });
    });
    let latest = '';
    (Array.isArray(c.sessions) ? c.sessions : []).forEach(s => {
      const d = (s && typeof s.date === 'string') ? s.date : ''; if (d && d > latest) latest = d;
    });
    if (!latest || latest < quietStr) {
      const openCount = actions.reduce((n, a) => n + (_fxOpen(effById.get(a.id).status) ? 1 : 0), 0);
      B.quiet.push({ custId: c.id, custName: c.name || 'Untitled project', latest, openCount });
    }
  });

  const cmpAction = (x, y) => _fxPriRank(x.priority) - _fxPriRank(y.priority)
    || ((x.due || '9999-99-99') < (y.due || '9999-99-99') ? -1 : (x.due || '9999-99-99') > (y.due || '9999-99-99') ? 1 : 0)
    || (x.actionId - y.actionId);
  ['overdue', 'dueweek', 'highpri', 'blocked', 'agenda'].forEach(k => B[k].sort(cmpAction));
  B.risks.sort((a, b) => b.score - a.score || b.imp - a.imp || a.id - b.id);
  B.quiet.sort((a, b) => ((a.latest || '0') < (b.latest || '0') ? -1 : (a.latest || '0') > (b.latest || '0') ? 1 : 0));
  return B;
}

function _fxChevron() { return `<svg class="fx-jump" width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M6 4l4 4-4 4"/></svg>`; }
function _fxToneStyle(tone) {
  if (tone === 'high')   return { dot: 'var(--high)',   cnt: 'color:var(--high);background:var(--high-bg);border:1px solid var(--high-border)' };
  if (tone === 'medium') return { dot: 'var(--medium)', cnt: 'color:var(--medium);background:var(--medium-bg);border:1px solid var(--medium-border)' };
  if (tone === 'low')    return { dot: 'var(--low)',    cnt: 'color:var(--low);background:var(--low-bg);border:1px solid var(--low-border)' };
  return { dot: 'var(--text3)', cnt: 'color:var(--text2);background:var(--surface2);border:1px solid var(--border)' };
}
function _fxRow(key, it, todayStr) {
  const esc = (typeof _escapeHtml === 'function') ? _escapeHtml : (s => s);
  if (key === 'risks') {
    return `<div class="fx-row" data-cust="${_fxJsAttr(it.custId)}" data-tab="risks" data-card="" onclick="_focusJumpEl(this)">`
      + `<span class="fx-pri h"></span><div class="fx-row-main"><div class="fx-row-title">${esc(it.title)}</div>`
      + `<div class="fx-row-meta"><span class="fx-proj">${esc(it.custName)}</span></div></div>`
      + `<div class="fx-right"><span class="fx-due">score ${it.score}</span>${_fxChevron()}</div></div>`;
  }
  if (key === 'quiet') {
    const wk = it.latest ? Math.floor((Date.parse(todayStr) - Date.parse(it.latest)) / 604800000) : null;
    const meta = (wk === null ? 'no sessions logged' : ('last session ' + wk + ' wk' + (wk === 1 ? '' : 's') + ' ago')) + ' · ' + it.openCount + ' open';
    return `<div class="fx-row" data-cust="${_fxJsAttr(it.custId)}" data-tab="" data-card="" onclick="_focusJumpEl(this)">`
      + `<span class="fx-pri q"></span><div class="fx-row-main"><div class="fx-row-title">${esc(it.custName)}</div>`
      + `<div class="fx-row-meta"><span>${esc(meta)}</span></div></div>`
      + `<div class="fx-right">${_fxChevron()}</div></div>`;
  }
  const over = it.due && it.due < todayStr;
  const dueLbl = _fxDueLabel(it.due, todayStr);
  const dueHtml = dueLbl ? `<span class="fx-due${over ? ' over' : ''}">${esc(dueLbl)}</span>` : '';
  let badge;
  if (key === 'blocked') badge = `<span class="fx-badge fx-b-block">Blocked</span>`;
  else if (key === 'agenda') badge = `<span class="fx-badge fx-b-agenda">On agenda</span>`;
  else badge = `<span class="fx-badge fx-b-open">${esc(it.status)}</span>`;
  return `<div class="fx-row" data-cust="${_fxJsAttr(it.custId)}" data-tab="actions" data-card="card-${it.actionId}" onclick="_focusJumpEl(this)">`
    + `<span class="fx-pri ${_fxPriClass(it.priority)}"></span><div class="fx-row-main"><div class="fx-row-title">${esc(it.title)}</div>`
    + `<div class="fx-row-meta"><span class="fx-proj">${esc(it.custName)}</span></div></div>`
    + `<div class="fx-right">${dueHtml}${badge}${_fxChevron()}</div></div>`;
}
function _fxBucketHtml(b, count, rows) {
  const t = _fxToneStyle(b.tone);
  return `<div class="fx-bucket${_fxCollapsed.has(b.key) ? ' collapsed' : ''}" data-bucket="${b.key}">`
    + `<div class="fx-bk-head" onclick="fxToggleBucket(this)"><span class="fx-bk-dot" style="background:${t.dot}"></span>`
    + `<span class="fx-bk-title">${b.title}</span><span class="fx-bk-count" style="${t.cnt}">${count}</span>`
    + `<span class="fx-bk-sub">${b.sub}</span>`
    + `<svg class="fx-chev" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 6l4 4 4-4"/></svg></div>`
    + `<div class="fx-bk-list">${rows}</div></div>`;
}
/* v13.1 (U05, ◆2): _fxCloneLogo and the #fxLogo / #ovLogo slots are gone. Of 195
   looked-up ids these two were the only ones never created, so the cloner ran twice per
   Focus render and did nothing both times. ◆2 chose removal over adding the slots: the
   logo-less Focus and Overview heads are the shipped, pixel-proofed look of the last ten
   versions, and a slot added now would change a screen nobody asked to change. */
/* v10.3 (W5): _fxThemeToggleHtml removed — never called. */
function renderFocus() {
  const host = document.getElementById('focusView'); if (!host) return;
  const B = _focusData();
  const counts = {}; Object.keys(B).forEach(k => counts[k] = B[k].length);
  const total = Object.keys(counts).reduce((a, k) => a + counts[k], 0);
  if (_fxFilter !== 'all' && !counts[_fxFilter]) _fxFilter = 'all';

  const head = `<div class="fx-head">`
    + `<div><div class="fx-title">Focus</div><div class="fx-sub">Across this programme — what needs you</div></div>`   /* v15.2: it has been programme-scoped since v11; "all projects" was a lie in a multi-programme portfolio */
    + `</div>`;

  if (total === 0) {
    host.innerHTML = head + `<div class="fx-empty">Nothing needs your attention right now — every project is clear.</div>`;
    return;
  }

  let chips = `<span class="fx-chip${_fxFilter === 'all' ? ' on' : ''}" data-f="all" onclick="fxSetFilter('all')">All</span>`;
  let buckets = '';
  _FX_BUCKETS.forEach(b => {
    if (!counts[b.key]) return;  // hide empty buckets + their chips
    chips += `<span class="fx-chip ${b.chip}${_fxFilter === b.key ? ' on' : ''}" data-f="${b.key}" onclick="fxSetFilter('${b.key}')"><b>${counts[b.key]}</b> ${b.label}</span>`;
    const rows = B[b.key].map(it => _fxRow(b.key, it, _fxISO(_fxToday()))).join('');
    buckets += _fxBucketHtml(b, counts[b.key], rows);
  });
  const ctl = `<div class="fx-ctl"><button onclick="fxFoldAll(true)">Collapse all</button><button onclick="fxFoldAll(false)">Expand all</button></div>`;
  host.innerHTML = head + `<div class="fx-filters">${chips}</div>` + ctl + buckets
    + `<div class="fx-empty" id="fxEmpty" style="display:none">Nothing in this bucket.</div>`;
  fxApplyFilter();
}

function fxToggleBucket(head) {
  const bk = head.parentNode;
  /* the class is the paint; the Set is the memory — written from the class so the two
     cannot drift (A53) */
  if (bk.classList.toggle('collapsed')) _fxCollapsed.add(bk.dataset.bucket);
  else _fxCollapsed.delete(bk.dataset.bucket);
}
function fxSetFilter(f) { _fxFilter = f; fxApplyFilter(); }
function fxApplyFilter() {
  const host = document.getElementById('focusView'); if (!host) return;
  host.querySelectorAll('.fx-chip').forEach(c => c.classList.toggle('on', c.dataset.f === _fxFilter));
  let shown = 0;
  host.querySelectorAll('.fx-bucket').forEach(bk => {
    const vis = (_fxFilter === 'all' || bk.dataset.bucket === _fxFilter);
    bk.style.display = vis ? '' : 'none';
    if (vis) shown++;
  });
  const empty = document.getElementById('fxEmpty'); if (empty) empty.style.display = shown ? 'none' : 'block';
}
function fxFoldAll(collapse) {
  const host = document.getElementById('focusView'); if (!host) return;
  host.querySelectorAll('.fx-bucket').forEach(bk => {
    if (bk.style.display === 'none') return;
    bk.classList.toggle('collapsed', collapse);
    if (collapse) _fxCollapsed.add(bk.dataset.bucket); else _fxCollapsed.delete(bk.dataset.bucket);
  });
}
function _focusJumpEl(el) { _focusJump(el.dataset.cust, el.dataset.tab || null, el.dataset.card || null); }
function _focusJump(custId, tab, card) {
  _focusPendingTab = tab || null; _focusPendingCard = card || null;
  _projReturn = 'focus';  /* v8.3.0: remember we arrived from Focus, for the return chip */
  gotoProject(custId);
}

function renderOverview() {
  const host = document.getElementById('overviewView');
  if (!host) return;
  const db = window.__DB__ || { projects: [] };
  _hdrSearchSync();   /* v16.5 (c) */
  const _allProj = _listOrderOrdered(_progProjects(true), 'project', activeProgrammeId);   /* v17.5 P4: ordered copy; source stays untouched */
  /* v16.5 (c): the same rule one level down - name or the visible id. */
  const all = _allProj.filter(c => c && _hdrMatch((c.name || '') + ' ' + _dispId('PRJ', c)));
  const _projHiddenBySearch = _allProj.length - all.length;
  const custs = all.filter(c => c && !c.archived);
  const archived = all.filter(c => c && c.archived);

  /* v10.4 (W3): the cards are gone — the single project table below carries every
     project. The empty state now depends on what is actually SHOWN: with archived
     projects present and the toggle on, the table is non-empty even with no live
     projects, so "No active projects" would be a lie sitting above a full table. */
  const _shownCount = custs.length + ((_ovShowArchived && archived.length) ? archived.length : 0);
  const body = _shownCount ? '' : `<div class="ov-empty">No active projects.</div>`;

  const archCount = archived.length;
  const toggleHtml = archCount
    ? `${_projHiddenBySearch ? `<span class="pf-search-note">${_projHiddenBySearch} hidden by your search `+ `<button type="button" class="pf-search-clear" onclick="hdrSearchClear()">Clear</button></span>` : ''}`
      + `<label class="ov-arch-toggle"><input type="checkbox" id="ovShowArchived" onchange="ovToggleArchived(this)"${_ovShowArchived ? ' checked' : ''}> Show archived (${archCount})</label>`
    : '';
  /* v12.3 (W4): Import and Export have moved to the PORTFOLIO toolbar. Both read and write THE
     FILE, and since v11 one file IS the whole portfolio - so they belong where the file lives,
     beside “Change data file”, which is the same reasoning v11.1 used for the data-source line.
     The code already agreed in a telling way: openExport lists __DB__.projects PORTFOLIO-WIDE,
     not _progProjects(), so this picker always offered other programmes’ projects - misleading
     from a programme view, correct one level up. The MODALS have not moved; only the openers. */
  /* v11.1 (W1): _dataFileBtnHtml() is NOT called here any more — the Change / Reconnect
     control belongs to the portfolio, which owns the file. Its renderer keeps ONE caller. */
  /* v17.5 SD-01: programme and project headers now mirror this file-level date. With parts,
     mergeParts supplies the newest confirmed savedAt across the loaded portfolio. */
  /* v12.3 (W2): the PROGRAMME manager, top-right, exactly where a project shows its PM.
     Editable only when the programme branch is usable - typing into a schema-rejected or
     unreadable file would be an edit that can never be saved (the ovProgName precedent).
     It sits INSIDE re-rendered HTML, so it follows handleProgrammeNameEdit's model: commit on
     blur, Enter blurs, and nothing re-renders while the field has focus. */
  const _pmgr = (_activeProg() && typeof _activeProg().manager === 'string' && _activeProg().manager.trim())
    ? _activeProg().manager.trim() : '—';
  const mgrHtml = `Programme Manager: <span class="editable-inline" id="ovProgManager"`
    + ((_activeProg() && _dbLoadOk) ? ` contenteditable="true" spellcheck="false" onblur="handleProgrammeManagerEdit(this)" onkeydown="if(event.key==='Enter'){event.preventDefault();this.blur();}" title="Who is accountable for this programme"` : '')
    + `>${_escapeHtml(_pmgr)}</span><br>`;
  /* v14 (W6): "As of" → "Last saved" — the third and last render site, and the one that prompted
     it: "As of" directly beneath "Programme Manager: <name>" reads as the MANAGER's tenure. */
  const asOfHtml = `<div class="header-meta ov-meta">${mgrHtml}Last saved ${_lastSavedSpanHtml('programmeLastSavedDisplay')}`
    /* v12.3 (W3): the version tag is gone from here - it describes the FILE, and the file is a
       portfolio-level fact (the same reasoning v11.1 used to move the data-source line). The DATE
       stays: it is the file’s currency, which is worth knowing at every level.
       ⚠ Read from APP_VERSION on the portfolio, never a literal, so it cannot go stale. */
    /* v11.1 (W1): the data-source line and its ℹ full path moved to the PORTFOLIO. Keeping
       them here implied one file per programme — the v10.5 model that v11 replaced. */
    + `</div>`;
  /* v10.0: the programme layer — masthead RAG + rationale, the project × KPI
     heatmap, and the benefits register. Rendered only when a programme branch
     exists (a schema-rejected file is never touched, so its landing page shows
     just the banner). The masthead RAG is a HUMAN call — never an average. */
  const progOk = !!(_activeProg() && _dbLoadOk);
  const needs = custs.filter(c => c.appMeta && c.appMeta.dashboard && c.appMeta.dashboard.rag
    && ['red', 'amber'].includes(c.appMeta.dashboard.rag.level)).length;
  const subHtml = `${custs.length} project${custs.length === 1 ? '' : 's'}`
    + ((progOk && needs) ? ` · ${needs} need${needs === 1 ? 's' : ''} attention` : '');
  const pragHtml = progOk
    ? `<div class="rag-card ov-prag"><span class="rag-pill rag-none" id="pmRagPill" onclick="cycleProgrammeRag()" role="button" tabindex="0" onkeydown="_keyActivate(event)" title="Programme status — your call, not an average of the grid (click to cycle)">Not set</span><div class="rag-rationale" id="pmRagRationale" contenteditable="true" spellcheck="false"></div></div>`
    : '';
  const heatHtml = progOk ? _ovProjectTableHtml(custs, archived, _ovShowArchived) : '';
  const dependencyMapHtml = progOk ? _programmeDependencyMapHtml() : '';
  const benefitsHtml = progOk
    ? `<div class="ov-benefits"><div class="ov-benefits-head">Programme benefits <span class="raid-count" id="ovBenefitsCount"></span><span class="ov-info-i" title="Projects deliver outputs; the PROGRAMME realises benefits — so the register lives here. Each benefit: the measurable outcome · measure with baseline → target (and now) · a status · an owner · the contributing projects (references — a deleted project drops off)." onclick="event.preventDefault();event.stopPropagation()">i</span></div><div id="ovBenefitsList"></div><div class="ov-benefits-empty" id="ovBenefitsEmpty" style="display:none;">No programme benefits yet — add the outcomes this programme exists to realise.</div><button class="add-btn" onclick="addProgrammeBenefit()">+ Add benefit</button></div>`
    : '';
  /* v10.5 (W1): the landing title is the PROGRAMME's own name, with `Portfolio` as the
     trail above it — the title says what you are looking at, the trail says where it
     sits. It replaces the hardcoded product name, which named the app rather than the
     content. Editable only when the programme branch is actually usable: typing a name
     into a schema-rejected or unreadable file would be an edit that can never be saved. */
  /* v10.5 (◆2): while the boot handle-restore is still in flight, the only name
     available is the sibling BOOTSTRAP file's — so hold the title blank (it keeps its
     box, so nothing jumps) rather than paint a programme name that may be the wrong
     one. The restore's .finally re-renders once it has settled. */
  const _pending = !_progTitleReady;
  const _pl = _pending ? { text: '', fallback: false } : _progLabel();
  const _editable = progOk && !_pending;
  const titleHtml = `<div class="ov-title${_pending ? ' ov-title-pending' : (_pl.fallback ? ' ov-title-unnamed' : '')}" id="ovProgName"`
    /* v12.3.1: `event` is passed so the handler can see where focus is heading
       (relatedTarget) and restore it after its re-render — otherwise a click straight to
       the manager field is swallowed. */
    + (_editable ? ` contenteditable="true" spellcheck="false" onblur="handleProgrammeNameEdit(this, event)" onkeydown="if(event.key==='Enter'){event.preventDefault();this.blur();}" title="Click to rename this programme"` : '')
    + `>${_pending ? '&nbsp;' : _escapeHtml(_pl.text)}</div>`
    /* v13.1 (A31): the second of the two. It told the user to click a FILE NAME that has
       not appeared anywhere since v11 retired one-file-per-programme; what is actually on
       screen, and what they click, is the title itself. */
    + ((_pl.fallback && _editable) ? `<div class="ov-title-hint">No name yet — click the title above to name this programme.</div>` : '');
  host.innerHTML = `<div class="ov-head">`
    + `<div class="ov-head-text">${titleHtml}`
    + `<div class="ov-sub">${subHtml}</div></div>`
    + `<div class="ov-toolbar"><button class="ov-btn ov-btn-primary" onclick="ovNewProject(this)">+ New project</button>`
    + (progOk ? `<button class="ov-btn" onclick="toggleProgrammeDependencies()" aria-expanded="${_predMapOpen ? 'true' : 'false'}">Dependencies</button>` : '')
    + (progOk ? _listOrderControlsHtml('project', activeProgrammeId) : '')
    + `${toggleHtml}</div>`
    + asOfHtml
    + `</div>`
    /* v11.1 (W1): the six full banners render on the PORTFOLIO only — it owns the data
       file now. A problem is signalled here by #fileAlertStrip instead: one sentence and
       one hop home, never a control. v11's lesson still holds and is why there is exactly
       ONE strip renderer rather than a second copy of the banners. */
    + pragHtml
    + heatHtml
    + dependencyMapHtml
    + benefitsHtml
    + body;
  /* v10.0: bind the programme pieces built above (fresh nodes on every render) */
  if (progOk) {
    _renderProgrammeRag();
    const rat = document.getElementById('pmRagRationale');
    if (rat) {
      rat.textContent = (_activeProg() || { rag: {} }).rag.rationale || '';
      _onBlurCommit(rat, () => {
        const v = (rat.textContent || '').trim();
        const _pb = _activeProg();
        if (_pb && v !== (_pb.rag.rationale || '')) { _pb.rag.rationale = v; markDirty(); }
      });
    }
    _renderProgrammeBenefits();
  }
}

/* + New project — inline name entry (replaces the button); Enter creates + drops in,
   Escape/blank cancels. A one-shot guard stops Enter-then-blur double-creating. */
function ovNewProject(btn) {
  let done = false;
  const input = document.createElement('input');
  input.className = 'ov-new-input';
  input.type = 'text';
  input.placeholder = 'New project name…';
  input.maxLength = 80;
  const commit = (cancel) => {
    if (done) return; done = true;
    const v = (input.value || '').trim();
    if (cancel || !v) { renderOverview(); return; }
    const id = createProject(v);
    if (id) gotoProject(id); else renderOverview();   // drop straight into the new project
  };
  input.addEventListener('keydown', e => {
    if (e.key === 'Enter') { e.preventDefault(); commit(false); }
    else if (e.key === 'Escape') { e.preventDefault(); commit(true); }
  });
  _onBlurCommit(input, () => commit(false));
  btn.replaceWith(input);
  input.focus();
}
function ovToggleArchived(cb) { _ovShowArchived = !!(cb && cb.checked); renderOverview(); }
/* ── per-project ⋯ management menu (card + breadcrumb) — kept separate from the
   switcher so a manage action is never a mis-click while switching. ── */
function _closeAllMenus() {
  const sw = document.getElementById('projSwitcher'); if (sw) sw.hidden = true;
  const am = document.getElementById('projMenu'); if (am) am.hidden = true;
  document.querySelectorAll('.ov-card-menu').forEach(m => m.hidden = true);
}
